import { Paper, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import type { Icon } from '@tabler/icons-react';

/** Hover elevation for the Home task grid (design-uplift goal, leverage point 3):
 * a scoped stylesheet, same precedent as CenteredCard's `.zs-auth-*` and Shell's
 * `.zs-nav-link` — Mantine v8's `styles` prop only accepts flat CSSProperties, no
 * pseudo-selectors, so a `:hover` transform/shadow needs a real `<style>` tag
 * rather than a theme override. Exported so the Home route can render it ONCE
 * near the task grid instead of once per card (six `TaskButton`s would otherwise
 * mean six duplicate `<style>` tags). Scoped to `.zs-task-card` — NOT a global
 * Paper hover, which would wrongly make every non-interactive stat tile look
 * clickable too. */
export const TASK_CARD_STYLES = `
.zs-task-card {
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
  height: 100%;
}
.zs-task-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--mantine-shadow-md);
  border-color: var(--mantine-primary-color-filled);
}
`;

/** A big, tappable home-screen task button (BUILD_SPEC.md §11 — large touch
 * targets, tablet-usable at the gatehouse). `color` tints the icon chip per
 * action family for scannability — deliberately drawn from Mantine's stock
 * palette (blue/grape/teal/indigo/violet/cyan…), NOT the app's amber/
 * brandGreen/brandOrange/brandRed tuples: those four are reserved for document
 * STATUS under the branded-neutral ruling (2026-08-24) — amber already means
 * "draft" on a purchase order elsewhere on this same screen's data, so reusing
 * it here as a decorative task-family color would quietly relabel "Silo
 * reading" as "this thing is in draft". */
export function TaskButton({
  label,
  to,
  icon: TaskIcon,
  description,
  color = 'graphite',
}: {
  label: string;
  to: string;
  icon: Icon;
  description?: string;
  color?: string;
}) {
  return (
    <UnstyledButton component={Link} to={to} style={{ display: 'block', height: '100%' }}>
      <Paper withBorder radius="lg" p="lg" className="zs-task-card" mih={140}>
        <Stack gap="sm" h="100%" justify="flex-start">
          <ThemeIcon size={48} radius="md" variant="light" color={color}>
            <TaskIcon size={26} stroke={1.5} />
          </ThemeIcon>
          <Stack gap={2}>
            <Text fw={600} size="lg">
              {label}
            </Text>
            {description && (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        </Stack>
      </Paper>
    </UnstyledButton>
  );
}
